import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Node, Rule, Ruleset } from "@aptsi-types";
import { environment } from "@env";
import { Observable } from "rxjs";
import { catchError, filter, map } from "rxjs/operators";
import { ErrorHandlingService } from "../shared/errorHandling/errorHandling.service";
import { API_ENDPOINTS } from "../ada-access-api-endpoints";
import { BehaviorSubject } from "rxjs";
import {
  AdaptiveAccessControlNode,
  AdaptiveAccessControlNodeConfig,
} from "@aptsi-types";
import { NavigationEnd, Router } from "@angular/router";
import { RuleService } from "../rules/rules.service";
import { NodeService } from "./node/node.service";

interface GetRulesetsResponse {
  rulesets: Ruleset[];
}

interface GetRulesetResponse {
  ruleset: Ruleset;
}

interface GetNodeResponse {
  node: Node;
}

interface GetNodesResponse {
  nodes: Node[];
}

@Injectable({
  providedIn: "root",
})
export class RulesetService {
  private node: Node;

  private readonly rulesetUrl = environment.apiUrl;
  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService,
    private router: Router,
    private nodeService: NodeService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.nodes.next([]);
      });
  }

  public nodes = new BehaviorSubject<any[]>([]);

  nodes$ = this.nodes.asObservable();

  getNodes(): any[] {
    return this.nodes.value;
  }

  addEntry = async (entry: any, parentId?: string) => {
    try {
      const currentNodes = this.nodes.value;

      if (parentId) {
        const addToParent = (parentId: string): boolean => {
          const recursiveSearch = (nodes: any[]): boolean => {
            for (let node of nodes) {
              if (!node.ruleId) {
                console.error("Node without ruleId found:", node);
                continue;
              }

              if (node.ruleId === parentId) {
                if (!node.children) {
                  node.children = [];
                }

                // Before adding, ensure no circular reference
                const newEntry = { ...entry }; // Create a shallow copy of the entry
                delete newEntry.children; // Ensure that we don't accidentally carry over children

                node.children.push(newEntry); // Add the entry as a child
                this.nodes.next([...currentNodes]); // Update the BehaviorSubject with the new structure
                return true;
              }

              if (node.children && recursiveSearch(node.children)) {
                return true;
              }
            }
            return false;
          };
          return recursiveSearch(currentNodes);
        };

        if (!addToParent(parentId)) {
          console.error(`Parent with ID ${parentId} not found.`);
        }
      } else {
        console.log("I am outside:");

        this.nodes.next([...currentNodes, entry]); // Add as top-level node
        console.log(this.nodes);
      }
    } catch (error) {
      console.error("Error adding entry:", error);
    }
  };

  private selectedEntries = new BehaviorSubject<any[]>([]);

  selectedEntries$ = this.selectedEntries.asObservable();

  getRulesets(currentPage: number): Observable<Ruleset[]> {
    return this.http
      .post<GetRulesetsResponse>(
        `${this.rulesetUrl}${API_ENDPOINTS.RULESET.FETCH}`,
        { currentPage }
      )
      .pipe(
        map((response) => response.rulesets),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getRulesetSearch(
    currentPage: number,
    searchText: String,
    filterByHeader: String
  ): Observable<Ruleset[]> {
    return this.http
      .post<GetRulesetsResponse>(
        `${this.rulesetUrl}${API_ENDPOINTS.RULESET.SEARCH}`,
        { currentPage, searchText, filterByHeader }
      )
      .pipe(
        map((response) => response.rulesets),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getAllRulesets(currentPage: number): Observable<Ruleset[]> {
    return this.http
      .post<GetRulesetsResponse>(
        `${this.rulesetUrl}${API_ENDPOINTS.RULESET.GETALL}`,
        { currentPage }
      )
      .pipe(
        map((response) => response.rulesets),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getRuleset(rulesetid: string): Observable<Ruleset> {
    return this.http
      .post<GetRulesetResponse>(
        `${this.rulesetUrl}${API_ENDPOINTS.RULESET.GET}`,
        { rulesetid }
      )
      .pipe(
        map((response) => response.ruleset),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  createRuleset(name: string, rulesetType: string,organizationId : string): Observable<Ruleset> {
    return this.http
      .post<Ruleset>(`${this.rulesetUrl}${API_ENDPOINTS.RULESET.CREATE}`, {
        name,
        rulesetType,
        organizationId
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  updateRuleset(
    rulesetid: string,
    name: string,
    rulesetType: string,
    organizationId : string
  ): Observable<Ruleset> {
    return this.http
      .post<Ruleset>(`${this.rulesetUrl}${API_ENDPOINTS.RULESET.UPDATE}`, {
        rulesetid,
        name,
        rulesetType,
        organizationId
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  deleteRuleset(rulesetid: string): Observable<Ruleset> {
    return this.http
      .post<Ruleset>(`${this.rulesetUrl}${API_ENDPOINTS.RULESET.DELETE}`, {
        rulesetid,
      })
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  createNodeFlow(RulesetId: string, nodes: Node[]): Observable<Node> {
    return this.http
      .post<GetNodeResponse>(`${this.rulesetUrl}${API_ENDPOINTS.NODE.CREATE}`, {
        RulesetId,
        nodes,
      })
      .pipe(
        map((response) => response.node),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  updateNodeFlow(rulesetId: string, nodes: Node[]): Observable<Node> {
    return this.http
      .post<GetNodeResponse>(`${this.rulesetUrl}${API_ENDPOINTS.NODE.UPDATE}`, {
        rulesetId,
        nodes,
      })
      .pipe(
        map((response) => response.node),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getNode(rulesetId: string): Observable<Node[]> {
    return this.http
      .post<GetNodesResponse>(`${this.rulesetUrl}${API_ENDPOINTS.NODE.GET}`, {
        rulesetId,
      })
      .pipe(
        map((response) => response.nodes),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  deleteNode(nodeId: string, rulesetId: string) {
    return this.http
      .post<GetNodeResponse>(`${this.rulesetUrl}${API_ENDPOINTS.NODE.DELETE}`, {
        nodeId,
        rulesetId,
      })
      .pipe(
        map((response) => response.node),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  saveFlow() {
    return this.http
      .post<string>(`${this.rulesetUrl}${API_ENDPOINTS.NODE.SAVE}`, {})
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }
}
